import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

// FDV (patch #46): backend de lealtad; resuelve qué producto necesita un código de producto (FDV-/BAL-).
const FDV_LOYALTY_API = 'https://loyalty.flordevenus.com.mx';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();
    // FDV (patch #33): emitir el resultado de aplicar un código al bus. La rama
    // exacta (aplicado / inválido / no-aplica-a-envío) solo se conoce acá.
    const fdvSource = this.closest('theme-drawer') ? 'cart_drawer' : 'cart_page';
    const fdvTrack = (name, extra) =>
      discountCodeValue && window.FDV?.track?.(name, { code: discountCodeValue, source: fdvSource, ...extra });

    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.includes(discountCodeValue)) return;

    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
    this.#fdvShowNotice(null);

    // FDV (12-oct, noche): los códigos del susurro (`NOMBRE-INMORTAL-xxx`) ya no existen en Shopify (la llave era un descuento de
    // envío que bloqueaba el pago). El acceso lo da el pase del navegador, así que un código tecleado a mano NO se manda a Shopify
    // (saldría «inválido» y parecería un rechazo): se guarda el pase, se refresca el carrito sin ese código y el motor agrega la Poción.
    const fdvPaseSusurro = /-INMORTAL-/i.test(discountCodeValue);
    if (fdvPaseSusurro) {
      try {
        localStorage.setItem('fdv_susurro_pass', String(Date.now()));
      } catch (e) {
        /* noop */
      }
      try {
        document.cookie = `fdv_susurro_pass=1; path=/; max-age=${3 * 86400}; SameSite=Lax`;
      } catch (e) {
        /* noop */
      }
      window.FDV?.track?.('fdv_susurro_code_typed', { code: discountCodeValue, source: fdvSource });
    }

    const allDiscountCodes = fdvPaseSusurro ? [...existingDiscounts] : [...existingDiscounts, discountCodeValue];
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: allDiscountCodes.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: allDiscountCodes.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      let data = await response.json();
      let fdvAddedTitle = null;

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        // FDV (patch #46): un código de producto se rechaza si el producto no está en el carrito. Si el
        // backend dice cuál es, se agrega solo y se reintenta; si algo falla, queda el error de siempre.
        const fdvRecovered = await this.#fdvAddMissingProduct(
          discountCodeValue,
          allDiscountCodes,
          data,
          abortController.signal
        );

        if (!fdvRecovered) {
          discountCode.value = '';
          this.#handleDiscountError('discount_code');
          fdvTrack('fdv_discount_code_rejected', { reason: 'invalid' });
          deferredPromise.resolve({
            cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
          });
          return;
        }

        data = fdvRecovered.data;
        fdvAddedTitle = fdvRecovered.title;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          fdvTrack('fdv_discount_code_rejected', { reason: 'not_applicable_shipping' });
          deferredPromise.resolve({
            cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
          });
          return;
        }
      }

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });
      // Clear the input explicitly: data-skip-node-update on <input name="discount"> means
      // morphSection no longer syncs the input value from the server-rendered empty state,
      // so without this the user's typed code stays in the field after a successful apply.
      discountCode.value = '';
      fdvTrack('fdv_discount_code_applied', {
        cart_total_discount: typeof data.total_discount === 'number' ? data.total_discount : null,
        currency: data.currency ?? null,
      });
      morphSection(this.dataset.sectionId, newHtml, { mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
      // FDV (patch #46): el aviso va DESPUÉS del morph (síncrono): el morph reconcilia los hijos aunque el nodo tenga data-skip-node-update.
      if (fdvAddedTitle) this.#fdvShowNotice(`Agregamos ${fdvAddedTitle} a tu carrito para que tu código aplique.`);
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to apply discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: existingDiscounts.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId], {
        mode: this.closest('theme-drawer') ? 'hydration' : 'full',
      });
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to remove discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * FDV (patch #46): agrega al carrito el producto que un código necesita y reintenta el código.
   * Devuelve `{ data, title }` si el código quedó aplicable; si no, deja el carrito como estaba y devuelve null.
   *
   * @param {string} code
   * @param {string[]} allDiscountCodes
   * @param {any} cartData - respuesta de /cart/update con el código rechazado.
   * @param {AbortSignal} signal
   */
  async #fdvAddMissingProduct(code, allDiscountCodes, cartData, signal) {
    let variantId = 0;
    try {
      const lookup = await fetch(`${FDV_LOYALTY_API}/public/coupon-product?code=${encodeURIComponent(code)}`, { signal });
      if (!lookup.ok) return null;
      const product = await lookup.json();

      variantId = Number(product.variant_id);
      if (!variantId || cartData.items.some((/** @type {{ variant_id: number }} */ item) => item.variant_id === variantId)) {
        return null; // el producto ya está: el rechazo es por otra razón
      }

      const added = await fetch(
        Theme.routes.cart_add_url,
        { ...fetchConfig('json', { body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }) }), signal }
      );
      if (!added.ok) return null; // p. ej. agotado

      const retried = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({ discount: allDiscountCodes.join(','), sections: [this.dataset.sectionId] }),
        }),
        signal,
      });
      const next = await retried.json();

      if (next.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ d) => d.code === code && d.applicable === false)) {
        await this.#fdvRemoveVariant(variantId);
        return null;
      }

      return { data: next, title: String(product.title || 'tu producto') };
    } catch (error) {
      if (variantId) await this.#fdvRemoveVariant(variantId);
      if (error instanceof Error && error.name === 'AbortError') throw error;
      return null;
    }
  }

  /** @param {number} variantId */
  async #fdvRemoveVariant(variantId) {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const line = cart.items.find((/** @type {{ variant_id: number }} */ item) => item.variant_id === variantId);
      if (!line) return;
      await fetch(Theme.routes.cart_change_url, {
        ...fetchConfig('json', { body: JSON.stringify({ id: line.key, quantity: 0 }) }),
      });
    } catch (e) {
      /* noop: peor caso, queda el producto en el carrito y el cliente lo ve */
    }
  }

  /** @param {string | null} message */
  #fdvShowNotice(message) {
    const notice = this.querySelector('[data-fdv-discount-notice]');
    if (!(notice instanceof HTMLElement)) return;
    const text = notice.querySelector('small');
    if (text) text.textContent = message || '';
    notice.classList.toggle('hidden', !message);
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');

    const errorMessage = type === 'discount_code' ? 'Invalid discount code' : 'Discount not applicable for shipping';
    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
